import { useState, useEffect, useRef } from 'react';
import { HelpCircle, X, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import API_BASE from '../../utils/apiBase';

/**
 * Professional, reusable Live Support Chat component.
 * - Movable (draggable) floating "?" button (emerald green, classic help style).
 * - Opens clean drawer for live chat with admin/support staff.
 * - Uses WebSocket for true real-time push (no polling).
 * - Auto-attaches context (e.g. current shift) on first message.
 * - Fully reusable for worker and future client dashboards via props.
 *
 * Props:
 * - userType: 'worker' | 'client'
 * - getAuthToken: () => string
 * - apiBase: string (optional; falls back to canonical API_BASE with /api prefix)
 * - initialContext: {type: 'shift', id, location, startTime} | null
 * - title: string
 * - onChatStarted: (chatId) => void
 */
export default function LiveSupportChat({
  userType = 'worker',
  getAuthToken,
  apiBase = '',
  initialContext = null,
  title = 'Live Support - Talk to an admin',
  onChatStarted,
  openRequest = 0,
  showLauncher = true,
}) {
  // Ensure we always have a proper base (https://api.../api) even if caller passes empty string.
  // This fixes worker chat send/WS when the wrapper used raw process.env before.
  const effectiveApiBase = apiBase || API_BASE;
  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const wsRef = useRef(null);
  const sendingRef = useRef(false);
  const readVersion = useRef(0);
  const [loadError, setLoadError] = useState('');
  const [sendError, setSendError] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const panelRef = useRef(null);
  const previousFocus = useRef(null);
  useEffect(() => { if (openRequest > 0) setOpen(true); }, [openRequest]);
  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement;
    panelRef.current?.focus();
    return () => { previousFocus.current?.focus?.(); };
  }, [open]);

  // Movable ? button - persisted position
  const [pos, setPos] = useState(() => {
    try {
      const saved = localStorage.getItem(`live_support_pos_${userType}`);
      if (saved) return JSON.parse(saved);
    } catch {}
    return { x: window.innerWidth - 64, y: window.innerHeight - 80 };
  });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, posX: 0, posY: 0 });

  const onMouseDown = (e) => {
    if (open) return;
    setDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY, posX: pos.x, posY: pos.y });
  };

  const onMouseMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    let newX = Math.max(0, Math.min(dragStart.posX + dx, window.innerWidth - 56));
    let newY = Math.max(0, Math.min(dragStart.posY + dy, window.innerHeight - 56));
    setPos({ x: newX, y: newY });
  };

  const onMouseUp = () => {
    if (dragging) {
      setDragging(false);
      try {
        localStorage.setItem(`live_support_pos_${userType}`, JSON.stringify(pos));
      } catch {}
    }
  };

  // Touch handlers for proper draggable ? on real mobile devices (iPhone Safari, Chrome mobile)
  const onTouchStart = (e) => {
    if (open) return;
    const touch = e.touches[0];
    setDragging(true);
    setDragStart({ x: touch.clientX, y: touch.clientY, posX: pos.x, posY: pos.y });
  };

  const onTouchMove = (e) => {
    if (!dragging) return;
    const touch = e.touches[0];
    const dx = touch.clientX - dragStart.x;
    const dy = touch.clientY - dragStart.y;
    let newX = Math.max(0, Math.min(dragStart.posX + dx, window.innerWidth - 56));
    let newY = Math.max(0, Math.min(dragStart.posY + dy, window.innerHeight - 56));
    setPos({ x: newX, y: newY });
  };

  const onTouchEnd = () => {
    if (dragging) {
      setDragging(false);
      try {
        localStorage.setItem(`live_support_pos_${userType}`, JSON.stringify(pos));
      } catch {}
    }
  };

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragging]);

  // Clamp saved position (from desktop) so button is never lost off-screen on mobile/tablet or after resize.
  // This fixes "have to zoom out to see the live chat in bottom right".
  useEffect(() => {
    const clampPos = () => {
      setPos((current) => {
        const vw = window.innerWidth || 375;
        const vh = window.innerHeight || 667;
        return {
          x: Math.max(8, Math.min(current.x, vw - 56)),
          y: Math.max(8, Math.min(current.y, vh - 56)),
        };
      });
    };
    clampPos();
    window.addEventListener('resize', clampPos);
    return () => window.removeEventListener('resize', clampPos);
  }, []);

  // WebSocket for true push (professional, no polling)
  const connectWS = (cid) => {
    if (wsRef.current) wsRef.current.close();
    const token = getAuthToken();
    if (!token || !cid) return;

    const wsUrl = `${effectiveApiBase.replace('http', 'ws')}/ws/support-chat/${cid}?token=${token}`;
    const newWs = new WebSocket(wsUrl);
    wsRef.current = newWs;

    newWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (wsRef.current === newWs && getAuthToken() === token && data.type === 'message' && data.message) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
        }
      } catch (e) {}
    };

    newWs.onclose = () => { if (wsRef.current === newWs) wsRef.current = null; };
  };

  const disconnectWS = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  // Load + WS connect. Auto-attach context on first send if provided.
  const loadChat = async () => {
    const version = ++readVersion.current;
    const token = getAuthToken();
    const current = () => version === readVersion.current && token === getAuthToken();
    setChatLoading(true);
    setLoadError('');
    try {
      const res = await fetch(`${effectiveApiBase}/worker/support-chat`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('History unavailable');
      const data = await res.json();
      if (!current()) return;
      if (!Array.isArray(data.messages || [])) throw new Error('Invalid history');
      {
        const cid = data.chat_id;
        if (cid) {
          setMessages(data.messages || []);
          connectWS(cid);
          if (onChatStarted) onChatStarted(cid);
        } else {
          setMessages([]);
        }
      }
    } catch (e) {
      if (current()) setLoadError('We couldn’t load your messages. Try refreshing them.');
    } finally {
      if (current()) setChatLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadChat();
    } else {
      disconnectWS();
    }
    return () => { readVersion.current += 1; disconnectWS(); };
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sendingRef.current || chatLoading || loadError) return;

    sendingRef.current = true;
    setSendError('');
    setSending(true);
    let messageToSend = text;

    // Professional auto-attach context on first message
    if (messages.length === 0 && initialContext) {
      const ctx = initialContext;
      if (ctx.type === 'shift') {
        messageToSend = `[Context: Shift at ${ctx.location || 'N/A'} starting ${ctx.startTime || 'N/A'} (ID: ${ctx.id || 'N/A'})]\n${text}`;
      } else {
        messageToSend = `[Context: ${JSON.stringify(ctx)}]\n${text}`;
      }
    }


    const token = getAuthToken();
    try {
      const res = await fetch(`${effectiveApiBase}/worker/support-chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: messageToSend }),
      });
      if (res.ok) {
        await res.json();
        if (getAuthToken() !== token) return;
        setInput('');
        if (openRef.current) await loadChat();
      } else {
        throw new Error(`Send failed with status ${res.status}`);
      }
    } catch (e) {
      if (getAuthToken() === token) setSendError('We couldn’t confirm your message was sent. Your draft is still here. Refresh messages before trying again.');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const buttonStyle = {
    position: 'fixed',
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    zIndex: 9990,
    cursor: dragging ? 'grabbing' : 'grab',
  };

  return (
    <>
      {showLauncher && (
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={buttonStyle}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0F5C5E] text-white shadow-lg hover:bg-[#0c4a4c] transition-colors touch-none"
        aria-label="Open live support chat (drag the ? to move)"
        data-testid="live-support-chat-button"
      >
        <HelpCircle className="h-6 w-6" />
      </button>
      )}

      {open && (
        <div ref={panelRef} role="dialog" aria-label={title} tabIndex={-1} onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }} className="fixed bottom-20 right-4 z-[9991] w-[min(380px,calc(100vw-2rem))] max-h-[min(520px,70vh)] flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
          <div className="bg-[#0F5C5E] px-4 py-3 text-white flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-[#D9F1F4]">Live Support</p>
              <p className="font-semibold">{title}</p>
            </div>
            <button onClick={() => setOpen(false)} className="min-h-11 min-w-11 flex items-center justify-center rounded hover:bg-white/15" aria-label="Close chat">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div role="log" aria-label="Support conversation" aria-live="polite" className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50 text-sm">
            {chatLoading ? <p role="status">Loading messages…</p> : null}
            {loadError ? <p role="alert">{loadError}</p> : null}
            {!chatLoading && !loadError && messages.length === 0 ? (
              <p className="text-center text-slate-500 text-xs py-4">Send the team a message. Replies appear here.</p>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`flex ${(m.from === 'me' || m.from === 'worker') ? 'justify-end' : ''}`}>
                  <div className={`max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 ${(m.from === 'me' || m.from === 'worker') ? 'bg-[#0F5C5E] text-white' : 'bg-white border'}`}>
                    {m.text}
                    <div className="text-[10px] opacity-70 mt-1 text-right">
                      {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))
            )}
            {sending && <div role="status" className="text-xs text-slate-500 pl-1">Sending...</div>}
          </div>

          <div className="border-t p-3 bg-white">
            {sendError ? <p role="alert" className="mb-2 text-sm text-red-700">{sendError}</p> : null}
            <Button variant="ghost" className="mb-2 min-h-11" onClick={loadChat} disabled={chatLoading || sending}>Refresh messages</Button>
            <div className="flex gap-2">
              <Textarea
                aria-label="Message to support"
                value={input}
                onChange={(e) => { setInput(e.target.value); setSendError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
                placeholder="Type your message..."
                className="flex-1 min-h-[60px] text-sm"
                disabled={sending}
              />
              <Button aria-label="Send message" onClick={send} disabled={!input.trim() || sending || chatLoading || !!loadError} size="sm" className="self-end min-h-11 min-w-11">
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5 text-center">Messages and replies stay here in CareOps.</p>
          </div>
        </div>
      )}
    </>
  );
}
